import type { GlobalBetSlipItem } from './GlobalBetSlipManager'
import type { Sportsbook } from '@/types/types'
import * as Accordion from '@radix-ui/react-accordion'
import { useState } from 'react'

import Bet365Logo from '../assets/bet365.svg?react'
import BetMGMLogo from '../assets/betmgm.svg?react'
import DraftKingsLogo from '../assets/draftkings.svg?react'
import FanDuelLogo from '../assets/fanduel.svg?react'
import { useGlobalBetSlip } from './GlobalBetSlipManager'
import { useUIPreferences } from './UIPreferencesManager'

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

// Sportsbook configuration
const sportsbookConfig = {
  FanDuel: {
    bgColor: 'bg-fanduel-secondary',
    logo: FanDuelLogo,
  },
  Bet365: {
    bgColor: 'bg-bet365-secondary',
    logo: Bet365Logo,
  },
  DraftKings: {
    bgColor: 'bg-draftkings-secondary',
    logo: DraftKingsLogo,
  },
  BetMGM: {
    bgColor: 'bg-betmgm-secondary',
    logo: BetMGMLogo,
  },
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatMoney(amount: number): string {
  let normalized = Number(amount)
  if (!Number.isFinite(normalized))
    normalized = 0
  if (Object.is(normalized, -0))
    normalized = 0
  return USD_FORMATTER.format(normalized)
}

function calculateSinglePayout(odds: { american: string, decimal: string }, stake: number): number {
  if (stake === 0)
    return 0

  const americanOdds = odds.american
  // Strip both ASCII '-' and Unicode '−' as well as '+' to get absolute value
  const cleanOdds = americanOdds.replace(/[+\-−]/g, '')
  const numericOdds = Number.parseFloat(cleanOdds) || 0

  if (numericOdds === 0)
    return 0

  const hasMinus = americanOdds.includes('-') || americanOdds.includes('−')
  return hasMinus
    ? stake * (100 / numericOdds)
    : stake * (numericOdds / 100)
}

function calculatePayout(bets: GlobalBetSlipItem[]) {
  return bets.reduce((total, bet) => {
    const stake = Number.parseFloat(bet.stake) || 0
    return total + calculateSinglePayout(bet.odds, stake)
  }, 0)
}

/**
 * Generate DraftKings deep link URL
 * DraftKings supports multiple selections in a single URL for parlay building
 */
function generateDraftKingsUrl(bet: GlobalBetSlipItem): string {
  // Check for specific conditions: Dutch GP 2025, Race Winner, Oscar Piastri, DraftKings
  if (bet.eventName === 'Dutch GP 2025'
    && bet.marketName === 'Race Winner'
    && bet.selectionName === 'Oscar Piastri'
    && bet.sportsbook === 'DraftKings') {
    const selectedData = [bet.selectedData]

    if (selectedData.length === 0)
      return ''

    const encodedOutcomeIds = selectedData.map(({ selectionId }) => encodeURIComponent(selectionId)).join('+')
    const preUrl = `https://sportsbook.draftkings.com/mybets?outcomes=${encodedOutcomeIds}`

    const params = new URLSearchParams({
      s: '134556326',
      wpcid: '420759',
      wpsrc: '2954',
      wpcn: 'ParlayBuilder',
      preurl: preUrl,
    })

    return `https://sportsbook.draftkings.com/gateway?${params.toString()}`
  }

  // Default return for all other cases
  return 'https://dksb.sng.link/As9kz/et51?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgate[…]mo1&wpscn=SHARE&wpcrn=xx&wpscid=xx&wpcrid=xx&_forward_params=1'
}

/**
 * Generate FanDuel deep link URL from selected data
 * FanDuel currently only supports single selections, so we use the most recent one
 */
function generateFanDuelUrl(_selectedData: { marketId: string, selectionId: string }[]): string {
  // if (selectedData.length === 0)
  //   return ''
  //
  // // FanDuel API currently only handles one selection, so we use the last one
  // const lastSelection = selectedData[selectedData.length - 1]
  // return `https://account.sportsbook.fanduel.com/addToBetslip?marketId=${lastSelection.marketId}&selectionId=${lastSelection.selectionId}`
  return 'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_41450b_95c_&affid=21211&siteid=41450&adid=95&c='
}

/**
 * Generate Bet365 deep link URL
 * Currently a placeholder - Bet365 deep linking needs to be implemented
 */
function generateBet365Url(_selectedData: { selectionId: string }[]): string {
  // if (selectedData.length === 0)
  //   return ''
  // return 'https://www.bet365.com/' // Placeholder implementation
  return 'https://www.bet365.com/olp/open-account?affiliate=365_03778260'
}

/**
 * Generate BetMGM deep link URL
 * https://{base-url}/en/sports?options=7453040-284980932-871949695
 * Deep link to the option "871949695" in the market "284980932" of the fixture "7453040" in English. You can specify multiple options in one deep link: Separate the ID triplets with commas.
 */
function generateBetMGMUrl(_selectionData: { optionId: string, marketId: string, fixtureId: string }[]): string {
  // if (selectionData.length === 0)
  //   return ''
  // const options = selectionData.map(({ optionId, marketId, fixtureId }) => {
  //   return `${fixtureId}-${marketId}-${optionId}`
  // }).join(',')
  // // Construct the base URL and append the options query
  // const url = new URL('https://www.nv.betmgm.com/en/sports')
  // url.searchParams.set('options', options)
  // return url.toString()
  return 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1735841'
}

function GlobalBetSlipOverlay() {
  const [openPanel, setOpenPanel] = useState<string>('')
  const { oddsFormat, setOddsFormat } = useUIPreferences()

  const {
    betSlip,
    lastAddedSportsbook,
    removeBet,
    updateStake,
    clearSportsbook,
  } = useGlobalBetSlip()

  const [prevLastAddedSportsbook, setPrevLastAddedSportsbook] = useState(lastAddedSportsbook)
  if (lastAddedSportsbook !== prevLastAddedSportsbook) {
    setPrevLastAddedSportsbook(lastAddedSportsbook)
    if (lastAddedSportsbook) {
      setOpenPanel(`panel-${lastAddedSportsbook}`)
    }
  }

  // Calculate total bets across all sportsbooks
  const allBets = [
    ...betSlip.DraftKings,
    ...betSlip.FanDuel,
    ...betSlip.Bet365,
    ...betSlip.BetMGM,
  ]

  // Don't render if no bets
  if (allBets.length === 0) {
    return null
  }

  const formatOddsDisplay = (odds: { american: string, decimal: string }): string => {
    if (oddsFormat === 'american') {
      return odds.american
    }
    else {
      return odds.decimal
    }
  }

  const handlePlaceIndividualBet = (bet: GlobalBetSlipItem) => {
    let url = ''

    // Generate URL for single bet by wrapping selectedData in array
    switch (bet.sportsbook) {
      case 'DraftKings':
        url = generateDraftKingsUrl(bet)
        break
      case 'FanDuel':
        url = generateFanDuelUrl([bet.selectedData])
        break
      case 'Bet365':
        url = generateBet365Url([bet.selectedData])
        break
      case 'BetMGM':
        url = generateBetMGMUrl([bet.selectedData])
        break
    }

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    else {
      console.warn(`No deep link URL available for ${bet.sportsbook}`)
    }
  }

  // Get sportsbooks that have bets
  const sportsbooks = Object.keys(betSlip).filter(
    sportsbook => sportsbook !== 'lastAddedSportsbook' && betSlip[sportsbook as Sportsbook].length > 0,
  ) as Array<keyof typeof sportsbookConfig>

  return (
    <div className="pointer-events-auto fixed bottom-0 left-0 right-0 z-50 w-full md:top-4 md:bottom-auto md:right-4 md:left-auto md:w-96 md:max-h-[500px]">
      <div className="backdrop-blur-sm bg-white/95 rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-h-[400px] md:max-h-[500px] flex flex-col">

        {/* Header with total bet count and odds format toggle */}
        <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Bet Slip</h3>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              {allBets.length}
              {' '}
              bet
              {allBets.length !== 1 ? 's' : ''}
            </span>
          </div>
          <select
            value={oddsFormat}
            onChange={e => setOddsFormat(e.target.value as 'american' | 'decimal')}
            className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="american">American</option>
            <option value="decimal">Decimal</option>
          </select>
        </div>

        {/* Accordion for sportsbooks */}
        <Accordion.Root
          type="single"
          collapsible
          value={openPanel}
          onValueChange={setOpenPanel}
          className="w-full overflow-y-auto flex-1"
        >
          {sportsbooks.map((sportsbook) => {
            const config = sportsbookConfig[sportsbook]
            const Logo = config.logo
            const sportsbookBets = betSlip[sportsbook]
            const panelId = `panel-${sportsbook}`
            const groupedBets = sportsbookBets.reduce((groups, bet) => {
              const key = `${bet.eventName}-${bet.marketName}`
              if (!groups[key])
                groups[key] = []
              groups[key].push(bet)
              return groups
            }, {} as Record<string, GlobalBetSlipItem[]>)
            const hasMultipleEvents = Object.keys(groupedBets).length > 1

            return (
              <Accordion.Item key={sportsbook} value={panelId} className="border-b border-gray-200 last:border-b-0">
                <Accordion.Header className="flex">
                  <Accordion.Trigger
                    className="flex flex-1 items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div className="flex items-center gap-3">
                      <Logo className="w-8 h-8" />
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{sportsbook}</span>
                        <span className="text-xs text-gray-600">
                          Winnings:
                          {' '}
                          <span className="font-bold text-green-600">
                            {formatMoney(calculatePayout(sportsbookBets))}
                          </span>
                        </span>
                      </div>
                      <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                        {sportsbookBets.length}
                      </span>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                        openPanel === panelId ? 'rotate-180' : ''
                      }`}
                    />
                  </Accordion.Trigger>
                </Accordion.Header>

                <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                  <div className="p-4 pt-0 space-y-4">

                    {/* Group bets by event for better organization */}
                    {Object.entries(groupedBets).map(([eventKey, eventBets]) => (
                      <div key={eventKey} className="space-y-2">

                        {/* Event header (only show if multiple events) */}
                        {hasMultipleEvents && eventBets[0] && (
                          <div className="text-xs font-medium text-gray-600 border-b border-gray-100 pb-1">
                            {eventBets[0].eventName}
                            {' '}
                            •
                            {eventBets[0].marketName}
                          </div>
                        )}

                        {/* Bets for this event */}
                        {eventBets.map(bet => (
                          <div key={bet.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate">
                                  {bet.selectionName}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {bet.eventName}
                                  {' '}
                                  •
                                  {bet.marketName}
                                </div>
                                <div className="text-sm font-semibold text-green-600 mt-1">
                                  {formatOddsDisplay(bet.odds)}
                                </div>
                                {bet.stake && Number.parseFloat(bet.stake) > 0 && (
                                  <div className="text-sm text-blue-600 font-medium mt-1">
                                    Winnings:
                                    {' '}
                                    {formatMoney(calculateSinglePayout(bet.odds, Number.parseFloat(bet.stake)))}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeBet(bet.id, bet.sportsbook)}
                                className="text-gray-400 hover:text-red-500 transition-colors duration-150 ml-2"
                                aria-label={`Remove ${bet.selectionName} bet`}
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Stake Input */}
                            <div className="flex items-center gap-2">
                              <label htmlFor={`stake-${bet.id}`} className="text-xs text-gray-600 whitespace-nowrap">
                                Stake:
                              </label>
                              <div className="flex-1 relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                                  $
                                </span>
                                <input
                                  id={`stake-${bet.id}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={bet.stake}
                                  onChange={e => updateStake(bet.id, e.target.value, bet.sportsbook)}
                                  className="w-full pl-6 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="0.00"
                                />
                              </div>
                              {/* Individual Place Bet Button */}
                              <div>
                                <button
                                  onClick={() => handlePlaceIndividualBet(bet)}
                                  className={`w-full ${config.bgColor} font-semibold py-2 px-3 rounded-lg hover:opacity-90 active:scale-95 transition-all duration-150 text-sm`}
                                  type="button"
                                >
                                  Place Bet
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* Footer Actions */}
                    <div className="flex justify-center pt-2 border-t border-gray-200">
                      <button
                        onClick={() => clearSportsbook(sportsbook)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:scale-95 transition-all duration-150 text-sm"
                        type="button"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            )
          })}
        </Accordion.Root>
      </div>
    </div>
  )
}

export default GlobalBetSlipOverlay
